import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Star, ChevronLeft, ChevronRight, MapPin, CheckCircle } from "lucide-react";

const testimonials = [
  {
    name: "Sarah M.",
    location: "Lenexa, KS",
    rating: 5,
    text: "Diane made tax season stress-free! Professional, friendly, and affordable. I've been coming back for years.",
  },
  {
    name: "Tom R.",
    location: "Kansas City, MO",
    rating: 5,
    text: "38 years of experience really shows. Best tax preparer in KC! Quick turnaround and always available to answer questions.",
  },
  {
    name: "Lisa K.",
    location: "Overland Park, KS",
    rating: 5,
    text: "Quick turnaround and great communication. Highly recommend! She found deductions I didn't even know about.",
  },
  {
    name: "Michael J.",
    location: "Shawnee, KS",
    rating: 5,
    text: "Personal attention you won't find at big chain stores. She takes time to understand your unique situation.",
  },
];

const getInitials = (name: string) => {
  const names = name.split(' ');
  return names.map(n => n[0]).join('');
};

const getGradientForIndex = (index: number) => {
  const gradients = [
    'from-primary to-secondary',
    'from-secondary to-accent',
    'from-accent to-primary',
    'from-primary via-secondary to-accent'
  ];
  return gradients[index % gradients.length];
};

export const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="relative">
      {/* Enhanced card with animations */}
      <Card className="p-10 bg-gradient-to-br from-card via-background to-card border-2 border-primary/10 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-1 relative overflow-hidden group">
        
        {/* Animated background effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-secondary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        {/* Verified badge */}
        <div className="absolute top-4 right-4 flex items-center gap-1 bg-green-500/10 px-3 py-1 rounded-full border border-green-500/20">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <span className="text-xs font-semibold text-green-600 dark:text-green-400">Verified Client</span>
        </div>
        
        {/* Large decorative quote mark */}
        <div className="absolute top-8 left-8 text-6xl text-primary/10 font-serif leading-none">"</div>
        
        <div className="relative z-10">
          {/* Star rating with score */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex gap-1">
              {[...Array(currentTestimonial.rating)].map((_, i) => (
                <Star 
                  key={i} 
                  className="h-6 w-6 fill-accent text-accent animate-scale-in" 
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </div>
            <span className="text-2xl font-bold text-accent">5.0</span>
          </div>
          
          {/* Testimonial text with better typography */}
          <blockquote className="text-xl text-center mb-8 text-foreground leading-relaxed relative">
            <span className="italic">"{currentTestimonial.text}"</span>
          </blockquote>
          
          {/* Profile section with avatar */}
          <div className="flex items-center justify-center gap-4">
            {/* Avatar with initials */}
            <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${getGradientForIndex(currentIndex)} flex items-center justify-center shadow-lg ring-4 ring-background`}>
              <span className="text-white font-bold text-lg">
                {getInitials(currentTestimonial.name)}
              </span>
            </div>
            
            <div className="text-left">
              <p className="font-bold text-lg text-secondary">{currentTestimonial.name}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {currentTestimonial.location}
              </p>
            </div>
          </div>
        </div>
        
        {/* Bottom decorative quote mark */}
        <div className="absolute bottom-8 right-8 text-6xl text-secondary/10 font-serif leading-none rotate-180">"</div>
      </Card>

      {/* Enhanced navigation with arrows and dots */}
      <div className="flex items-center justify-center gap-4 mt-8">
        <button 
          onClick={goToPrevious}
          className="w-10 h-10 rounded-full bg-primary/10 hover:bg-primary hover:text-white transition-all duration-300 flex items-center justify-center"
          aria-label="Previous testimonial"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <div className="flex gap-2">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? "w-10 bg-gradient-to-r from-primary to-secondary shadow-lg"
                  : "w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50 hover:w-6"
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
        
        <button 
          onClick={goToNext}
          className="w-10 h-10 rounded-full bg-primary/10 hover:bg-primary hover:text-white transition-all duration-300 flex items-center justify-center"
          aria-label="Next testimonial"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};
